import '@shui/core/sh-datepicker';

import {
    Component,
    input,
    booleanAttribute,
    ChangeDetectionStrategy,
    afterEveryRender,
    ElementRef,
    inject
} from '@angular/core';

@Component({
    selector: 'sh-datepicker',
    template: '<ng-content />',
    imports: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        '[attr.active]': 'active() ? "" : null',
        '[attr.condensed]': 'condensed() ? "" : null',
        '[attr.disabled]': 'disabled() ? "" : null',
        '[attr.editable]': 'editable() ? "" : null',
        '[attr.error]': 'error() ? "" : null',
        '[attr.error-message]': 'errorMessage() ?? null',
        '[attr.first-day-of-week]': 'firstDayOfWeek() ?? null',
        '[attr.format]': 'format() ?? null',
        '[attr.from-date]': 'fromDate() ?? null',
        '[attr.future-date-disable]': 'futureDateDisable() ? "" : null',
        '[attr.hint]': 'hint() ?? null',
        '[attr.label]': 'label() ?? null',
        '[attr.locale]': 'locale() ?? null',
        '[attr.mandatory]': 'mandatory() ? "" : null',
        '[attr.max]': 'max() ?? null',
        '[attr.min]': 'min() ?? null',
        '[attr.months]': 'months() ?? null',
        '[attr.no-border]': 'noBorder() ? "" : null',
        '[attr.no-clear]': 'noClear() ? "" : null',
        '[attr.past-date-disable]': 'pastDateDisable() ? "" : null',
        '[attr.range]': 'range() ? "" : null',
        '[attr.readonly]': 'readonly() ? "" : null',
        '[attr.require-apply]': 'requireApply() ? "" : null',
        '[attr.responsive]': 'responsive() ?? null',
        '[attr.safety]': 'safety() ? "" : null',
        '[attr.scrollpicker]': 'scrollpicker() ?? null',
        '[attr.separator]': 'separator() ?? null',
        '[attr.standardValue]': 'standardValue() ?? null',
        '[attr.success]': 'success() ? "" : null',
        '[attr.to-date]': 'toDate() ?? null',
        '[attr.value]': 'value() ?? null'
    }
})
export class ShDatepickerComponent {
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

    /** Whether the datepicker overlay is opened or not. */
    public readonly active = input(false, { transform: booleanAttribute });

    /** If set, then the condensed format of datepicker is used to display information. */
    public readonly condensed = input(false, { transform: booleanAttribute });

    /**
     * If set, won't allow mouse events and disabled styles will be applied to the datepicker input.
     */
    public readonly disabled = input(false, { transform: booleanAttribute });

    /**
     * If set to true, the user will be able to enter the date manually along with the selection in
     * the calendar.
     */
    public readonly editable = input(false, { transform: booleanAttribute });

    /** If set to true, the datepicker  displays styles for invalid input. */
    public readonly error = input(false, { transform: booleanAttribute });

    /**
     * Displays a message when error property is set to true and when error-message is not
     * undefined.
     */
    public readonly errorMessage = input<string | number | undefined>(undefined);

    /**
     * Determines which day appears as the first column in the date picker's calendar view. Accepts
     * `sunday`, `monday`, `tuesday`, `wednesday`, `thursday`, `friday`, or `saturday` (case-
     * insensitive). When set, it will override the week start day defined by the `locale` setting.
     */
    public readonly firstDayOfWeek = input<string | number | undefined>(undefined);

    /**
     * Defines the format of the date to be displayed in input. Any combination of the parts `D`
     * ,`DD`,`M`, `MM`,`MMM` and `YYYY` can be a format. `D` and `M` stands for dates and months
     * whose value if less than 10, will have only 1 character. (example: `2`) and will have two
     * characters for values greater than 10 (example: `12`) . `DD` and `MM` stands for dates and
     * months whose value if less than 10, will have 2 characters. (example: `02`) and will have two
     * characters for values greater than 10 (example: `12`). `MMM` stands for month-name. (Examples
     * of valid format : `MMDDYYYY`,`MMMDD`,`MMMYYYYDD` , etc) (Examples of invalid format :
     * `YYYYMYYYY`, `YYYYmmdd` , etc)
     */
    public readonly format = input<string | number | undefined>(undefined);

    /**
     * The start date in a range value. This is a readonly property. Setting fromDate property will
     * not reflect in value property.
     */
    public readonly fromDate = input<string | number | undefined>(undefined);

    /** If true, future dates will be disabled. */
    public readonly futureDateDisable = input(false, { transform: booleanAttribute });

    /**
     * Displays a message when error property is set to true and when error-message is not an empty
     * string.
     */
    public readonly hint = input<string | number | undefined>(undefined);

    /** Defines the label of the datepicker input. */
    public readonly label = input<string | number | undefined>(undefined);

    /**
     * The locale value used for formatting the month and week days in the datepicker views. Locale
     * will not change based on browser language automatically. The implementing project is
     * responsible for setting the locale. The officially supported and tested locale values are
     * `en`, `de`, `es`, `fr`, `ja`, `zh`, `ru-Ru`, `it-IT`, `ko-KR`, `pt`, `pt-BR`, `sv-SE`, `nl-
     * NL`, `fi-FI`, `no-NO`, `pl-PL`, `tr-TR`
     */
    public readonly locale = input<string | number | undefined>(undefined);

    /** If set to true, sets an asterisk near to the label. */
    public readonly mandatory = input(false, { transform: booleanAttribute });

    /** Dates above this date will be disabled. Has to be in the format YYYY-MM-DD. */
    public readonly max = input<string | number | undefined>(undefined);

    /** Dates below this date will be disabled. Has to be in the format YYYY-MM-DD. */
    public readonly min = input<string | number | undefined>(undefined);

    /** Defines the number of months to be shown in datepicker either 2 or 3 months. */
    public readonly months = input<string | number | undefined>(undefined);

    /** If set to true, the bottom border will not be displayed. */
    public readonly noBorder = input(false, { transform: booleanAttribute });

    /** If set to `true`, the clear functionality is disabled. */
    public readonly noClear = input(false, { transform: booleanAttribute });

    /** If true, past dates will be disabled. */
    public readonly pastDateDisable = input(false, { transform: booleanAttribute });

    /**
     * If set, allows to select date range (from and to date). A mix of `-` and any other characters
     * (including `-` itself) (example: `--` or `- `) results in incorrect behaviour when range
     * property is used. So these separators must be avoided when using range property. Atmost 1
     * instance of `-` (example: `separator="-"`) can be used as separator when range property is
     * used.
     */
    public readonly range = input(false, { transform: booleanAttribute });

    /** If true, datepicker cannot be modified. */
    public readonly readonly = input(false, { transform: booleanAttribute });

    /**
     * If set to `true`, datepicker will have two buttons `Cancel` and `Apply` or can be provided
     * via slots: `cancel-button` and `apply-button` respectively. This will prevent the datepicker
     * from closing and applying the value after selecting the range. Instead it will apply the
     * value only on clicking the `Apply` button. Clicking on the `Cancel` button or clicking
     * outside the datepicker, closes it.
     */
    public readonly requireApply = input(false, { transform: booleanAttribute });

    /** Defines if Datepicker will use alternate mobile version or not. */
    public readonly responsive = input<string | number | undefined>(undefined);

    /**
     * If set to true, the datepicker  displays styles for safety-relevant input (yellow outline).
     */
    public readonly safety = input(false, { transform: booleanAttribute });

    /**
     * Enables a scrollable date picker view. In responsive mode, displays in a drawer.  In non-
     * responsive mode, shows in a popover overlay. This non-responsive behavior is experimental and
     * subject to change based on evaluation.
     */
    public readonly scrollpicker = input<string | number | undefined>(undefined);

    /**
     * Defines the separator of the date to be displayed in input. Separator can be any string of
     * any length. Empty-space character `''` (no-separator condition) is not allowed. Blank space
     * character `' '` is allowed. (Read the description of `range` property for the usage of `-` as
     * a separator)
     */
    public readonly separator = input<string | number | undefined>(undefined);

    /**
     * Array of standard Date object representation of the value or values (in case of range
     * datepicker) present in the `value` property. If the value is invalid or empty, this will be
     * an empty array. This array get updated on every change of value property.
     */
    public readonly standardValue = input<string | number | undefined>(undefined);

    /** If set to true, the datepicker  displays styles for valid input. */
    public readonly success = input(false, { transform: booleanAttribute });

    /**
     * The end date in a range value. This is a readonly property. Setting toDate property will not
     * reflect in value property.
     */
    public readonly toDate = input<string | number | undefined>(undefined);

    /** The value of the datepicker in the specified format. */
    public readonly value = input<string | number | undefined>(undefined);

    constructor() {
        afterEveryRender(() => this.syncClearButtonAccessibility());
    }

    private syncClearButtonAccessibility(): void {
        setTimeout(() => {
            this.elementRef.nativeElement.shadowRoot
                ?.querySelector<HTMLElement>('sh-input-text.input')
                ?.shadowRoot?.querySelector<HTMLElement>('#clear')
                ?.setAttribute('tabindex', '0');
        });
    }
}
